import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Plot from 'react-plotly.js'
import { selectRTFO, setCurrentStageName } from '../../reducers/rtfoSlice'
import { selectUser } from '../../reducers/userSlice'
import { chartColors } from '../../helpers/chartColors'
import { useTheme } from '@mui/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { CircularProgress } from '@mui/material'

import { useSocket } from '../../context/useSocket.js'
import { rtfoConversionFactors } from './rtfoConversionFactors.js'

export const RTFOHistoricMonitorChart = ({ monitorData, isLoadingMonitorData, setMonitorUWI }) => {
  const { socket } = useSocket();

  const dispatch = useDispatch();

  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const stageData = currentRTFO?.stageData;
  const units = currentRTFO?.settings?.units || 'metric';

  const darkMode = user?.value?.darkMode;

  const backgroundData = monitorData;

  const startStageName = currentRTFO?.stages?.slice(-6)?.[0];
  const endStageName = currentRTFO?.stages?.slice(-1)?.[0];

  // the start and end timestamps of the stage range covered by the window
  const windowTimestampRange = [
    currentRTFO?.stageData?.[startStageName]?.stageStartTimestamp,
    currentRTFO?.stageData?.[endStageName]?.stageEndTimestamp || currentRTFO?.stageData?.[startStageName]?.stageStartTimestamp + currentRTFO?.stageData?.[startStageName]?.metrics?.stageTime * 60 * 1000
  ];

  const selectorOptions = {
    buttons: [
      {
        step: 'hour',
        stepmode: 'backward',
        count: 1,
        label: '1h',
        color: 'red'
      },
      {
        step: 'hour',
        stepmode: 'backward',
        count: 6,
        label: '6h',
      },
      {
        step: 'hour',
        stepmode: 'backward',
        count: 24,
        label: '24h',
      },
      {
        step: 'day',
        stepmode: 'backward',
        count: 3,
        label: '3d',
      },
      {
        step: 'day',
        stepmode: 'backward',
        count: 7,
        label: '7d',
      },
      {
        step: 'all',
      },
    ],
    bgcolor: darkMode ? '#333' : '#e6e6e6',
    activecolor: darkMode ? '#111' : '#d9d9d9',
    font: {
      color: darkMode ? '#fff' : '#000'
    }
  };

  const layout = {
    yaxis: {
      // Pressure y axis
      linecolor: chartColors[4],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[4],
      title: {
        text: `Monitor Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        font: {
          size: 12,
        },
      },
      position: 0,
      autorange: true,
      rangemode: 'nonnegative',
      fixedrange: false
    },
    yaxis2: {
      // Pressure y axis
      linecolor: chartColors[2],
      showgrid: false,
      color: chartColors[2],
      title: {
        text: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        font: {
          size: 12,
        },
      },
      side: 'left',
      position: 0,
      anchor: 'x',
      range: units === 'field' ? [0, 15000] : [0, 100],
      rangemode: 'nonnegative',
      fixedrange: false
    },
    yaxis3: {
      // Rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        font: {
          size: 12,
        },
      },
      side: 'right',
      anchor: 'free',
      overlaying: 'y1',
      position: 1,
      range: units === 'field' ? [0, 150] : [0, 25],
      rangemode: 'nonnegative',
    },
    yaxis4: { // Pressure derivative y axis
      linecolor: chartColors[7],
      showgrid: false,
      color: chartColors[7],
      title: {
        text: `Pressure Derivative (${units === 'field' ? 'psi/min' : 'MPa/min'})`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: [-4, 4],
      side: 'right',
      overlaying: 'y'
    },
    xaxis: {
      // Time x axis
      title: {
        text: 'Date',
        font: {
          size: 12,
        },
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      type: 'date',
      domain: [downMD || window.innerHeight < 500 ? 0.07 : 0.05, downMD || window.innerHeight < 500 ? 0.93 : 0.95],
      showspikes: true,
      spikethickness: 2,
      spikedash: 'dot',
      spikecolor: '#999999',
      spikemode: 'across',
      rangeslider: { thickness: 0.15 },
      range: [windowTimestampRange[0], windowTimestampRange[1]],
      // fixedrange: true,
      rangeselector: selectorOptions,
    },
    hovermode: 'closest',
    // hoverdistance: 5, // Distance to show hover label of data point
    // spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: 'h',
      yanchor: 'top',
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
      },
      y: -0.8,
    },
    shapes: [],
    annotations: [],
    uirevision: true,
    margin: { t: 60, l: 60, r: 60, b: 50 },
    images: [
      {
        // x: 0.035,
        x: 1,
        y: 1.025,
        sizex: downMD || window.innerHeight < 500 ? 0.1 : 0.05,
        sizey: 0.1,
        source: `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: 'page',
        yref: 'page',
        xanchor: 'right',
        yanchor: 'bottom',
        opacity: 0.75,
      },
    ],
    plot_bgcolor: 'transparent',
    paper_bgcolor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
  }

  // add stage annotations
  if (stageData) {
    const stageNames = Object.keys(stageData)

    for (const stageName of stageNames) {
      const stage = stageData?.[stageName]

      layout.shapes.push({
        // stage line
        type: 'rect',
        yref: 'paper',
        xref: 'x',
        x0: stage?.stageStartTimestamp,
        y0: 0,
        x1:
          stage?.stageEndTimestamp ||
          stage?.stageStartTimestamp + stage?.metrics?.stageTime * 60 * 1000,
        y1: 1,
        opacity: 0.1,
        fillcolor:
          stageName === currentRTFO?.currentStageName
            ? chartColors[1]
            : chartColors[0],
        line: {
          width: 2,
          color:
            stageName === currentRTFO?.currentStageName
              ? chartColors[1]
              : chartColors[0],
        },
      })
      layout.annotations.push({
        text: stageName?.split('_')?.slice(1)?.join(' '),
        font: {
          size: 10,
          color:
            stageName === currentRTFO?.currentStageName
              ? chartColors[1]
              : darkMode
                ? '#fff'
                : chartColors[0],
          weight: 'bold',
        },
        textangle: '-90',
        showarrow: false,
        align: 'center',
        x:
          (stage?.stageStartTimestamp +
            (stage?.stageEndTimestamp ||
              stage?.stageStartTimestamp +
                stage?.metrics?.stageTime * 60 * 1000)) /
          2, //position in x domain
        y: 0.99, //position in y domain
        xref: 'x',
        yref: 'paper',
        ay: -40,
        ax: -40,
      })
    }
  }

  const data = useMemo(() => {
    try {
      // add traces
      if (!stageData || Object.keys(stageData)?.length === 0) return [];
      
      let traces = [];
      
      // plot pumping data
      const stageNames = Object.keys(stageData);
      // add stages with pumping data (coldbore data)
      for (const stageName of stageNames?.filter(
        (s) => stageData?.[s]?.coldboreData,
      ) || []) {
        // add pressure and rate traces
        let pumpingData = [];
        let XPressure = [];
        let YPressure = [];
        let XRate = [];
        let YRate = [];
        let XPressurePrime = [];
        let YPressurePrime = [];
        const stageIndex = stageNames.indexOf(stageName);

        const stage = stageData[stageName];
        pumpingData = resampleTimeSeries(
          stage.coldboreData,
          stage.highResolutionData ? 30 : 3,
        );

        pumpingData?.forEach((d) => {
          XPressure.push(d.pressure > 0 ? d.timestamp : null);
          YPressure.push(d.pressure > 0 ? (d.pressure * (units === 'field' ? rtfoConversionFactors.pressure : 1)) : null);
          XRate.push(d.rate > 0 ? d.timestamp : null);
          YRate.push(d.rate > 0 ? (d.rate * (units === 'field' ? rtfoConversionFactors.rate : 1)) : null);
          XPressurePrime.push(d.pressurePrime > 0 ? d.timestamp : null);
          YPressurePrime.push(d.pressurePrime > 0 ? (d.pressurePrime * (units === 'field' ? rtfoConversionFactors.pressure : 1)) : null);
        });

        traces.push({
          // pressure trace
          x: XPressure,
          y: YPressure,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y2',
          name: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[2],
          },
          opacity: 0.5,
          marker: {
            color: 'transparent'
          },
          connectgaps: false,
          hoverformat: '.2f',
          legendgroup: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          showlegend: stageIndex === 0,
        });

        traces.push({
          // rate trace
          x: XRate,
          y: YRate,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y3',
          name: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 2,
            color: chartColors[3],
          },
          opacity: 0.5,
          marker: {
            color: 'transparent'
          },
          connectgaps: false,
          hoverformat: '.2f',
          legendgroup: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          showlegend: stageIndex === 0,
        });

        traces.push({
          // casing pr. deriv. trace
          x: XPressurePrime,
          y: YPressurePrime,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y4',
          name: `Pressure Derivative (${units === 'field' ? 'psi/min' : 'MPa/min'})`,
          line: {
            width: 2,
            color: chartColors[7],
          },
          opacity: 0.5,
          marker: {
            color: 'transparent'
          },
          connectgaps: false,
          hoverformat: '.2f',
          legendgroup: `Pressure Derivative (${units === 'field' ? 'psi/min' : 'MPa/min'})`,
          showlegend: stageIndex === 0,
        });
      };

      // add stages without pumping data (coldbore data)
      for (const stageName of stageNames?.filter(
        (s) => !stageData?.[s]?.coldboreData,
      ) || []) {
        const stageStartTimestamp =
          stageData?.[stageName]?.stageStartTimestamp;
        // end timestamp is either the end of the stage or the next 60 ?minutes?
        const stageEndTimestamp =
          stageData?.[stageName]?.stageEndTimestamp ||
          stageData?.[stageName]?.stageStartTimestamp +
            stageData?.[stageName]?.metrics?.stageTime * 60 * 1000;
        const backgroundX = [
          stageStartTimestamp,
          stageStartTimestamp + 100,
          stageEndTimestamp,
          stageEndTimestamp + 100,
        ];
        const backgroundYPressure = [
          0,
          (stageData?.[stageName]?.metrics?.averagePumpingPressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1),
          (stageData?.[stageName]?.metrics?.averagePumpingPressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1),
          0,
        ];
        const backgroundYRate = [
          0,
          (stageData?.[stageName]?.metrics?.averagePumpingRate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1),
          (stageData?.[stageName]?.metrics?.averagePumpingRate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1),
          0,
        ];

        const stageIndex = stageNames
          ?.filter((s) => !stageData?.[s]?.coldboreData)
          ?.indexOf(stageName);

        traces.push({
          // background trace
          x: backgroundX,
          y: backgroundYPressure,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y2',
          name: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 1,
            color: chartColors[2],
            dash: 'dot',
          },
          opacity: 0.5,
          marker: {
            color: 'transparent',
          },
          connectgaps: true,
          hoverformat: '.2f',
          legendgroup: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          showlegend: stageIndex === 0
        });

        traces.push({
          // background trace
          x: backgroundX,
          y: backgroundYRate,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y3',
          name: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 1,
            color: chartColors[3],
            dash: 'dot',
          },
          opacity: 0.5,
          marker: {
            color: 'transparent',
          },
          connectgaps: true,
          hoverformat: '.2f',
          legendgroup: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          showlegend: stageIndex === 0
        });
      };
      
      const allMonitorWells = backgroundData ? Object.keys(backgroundData) : [];

      // plot background monitor data
      if (allMonitorWells?.length > 0) {
       
        for (const monitorWell of allMonitorWells) {
          const monitorWellData = backgroundData[monitorWell];

          let YCasing = [];
          let YTubing = [];
          let X = [];
          for (let i = 0; i < monitorWellData?.length; i++) {
            const point = monitorWellData?.[i];
            YCasing.push((point.casingPressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
            YTubing.push((point.tubingPressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
            X.push(new Date(point.timestamp)?.getTime());
          };


          traces.push({
            // casing pressure trace
            x: X,
            y: YCasing,
            mode: 'line',
            type: 'scatter',
            xaxis: 'x1',
            yaxis: 'y1',
            name: `${monitorWell}-Casing Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
            line: {
              width: 2,
              color: chartColors[6],
              opacity: 0.9,
            },
            marker: {
              color: 'transparent'
            },
            connectgaps: false,
            hoverformat: '.2f',
            legendgroup: `${monitorWell}-Casing Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          })

          traces.push({
            // tubing pressure trace
            x: X,
            y: YTubing,
            mode: 'line',
            type: 'scatter',
            xaxis: 'x1',
            yaxis: 'y1',
            name: `${monitorWell}-Tubing Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
            line: {
              width: 2,
              color: chartColors[8],
              opacity: 0.9,
            },
            marker: {
              color: 'transparent'
            },
            connectgaps: false,
            hoverformat: '.2f',
            legendgroup: `${monitorWell}-Tubing Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          });
        };
      };

      return traces;
    } catch (error) {
      console.log(error);
      return [];
    };
  }, [backgroundData, stageData, units]);

  const debouncedHandleRangeChange = debounce(handleRangeChange, 500); // 500ms delay

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename:
        'RTFO-Historic-Monitor' + new Date().toISOString().split('T')[0],
      height: 550,
      width: 960,
      scale: 1, // Multiply title/legend/axis/canvas sizes by this factor
    },
  };

  try {
    return (<div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Plot
        data={data}
        layout={layout}
        config={config}
        useResizeHandler={true}
        style={{ width: '100%', height: '100%' }}
        onRelayout={handleSliderChange}
        onClick={handleClick}
      />

      {isLoadingMonitorData && (
        <div
          style={{
            position: 'absolute',
          }}
        >
          <CircularProgress
            style={{ color: darkMode && '#fff', width: 32, height: 32 }}
          />
        </div>
      )}
    </div>);
  } catch (error) {
    return <></>;
  };

  // Input debounce function
  function debounce(func, delay) {
    let timerId
    return function (...args) {
      clearTimeout(timerId)
      timerId = setTimeout(() => {
        func.apply(this, args)
      }, delay)
    }
  }

  // Event handler function
  function handleRangeChange(start, end) {
    try {
      const startTimestamp = new Date(start)?.getTime();
      const endTimestamp = new Date(end)?.getTime();

      // stages within the window
      const windowStageNames = Object.keys(currentRTFO?.stageData)
        ?.sort(
          (a, b) =>
            currentRTFO?.stageData?.[a]?.stageStartTimestamp -
            currentRTFO?.stageData?.[b]?.stageStartTimestamp,
        )
        ?.filter(
          (s) =>
            currentRTFO?.stageData?.[s]?.stageEndTimestamp >= startTimestamp &&
            currentRTFO?.stageData?.[s]?.stageStartTimestamp <= endTimestamp,
        );

      // stages without pumping data
      const stageNamesWithoutPumpingData = windowStageNames?.filter(
        (s) => !currentRTFO?.stageData?.[s]?.coldboreData,
      );

      if (socket?.readyState !== 1) return;

      if (stageNamesWithoutPumpingData?.length > 0) {
        socket.send(JSON.stringify({
          messageType: 'getResampledStagesData',
          stageNames: stageNamesWithoutPumpingData,
          userEmail: user?.value?.email
        }));
      };

    } catch (error) {
      console.log(error)
      return []
    };
  };

  // Slider handler function
  function handleSliderChange(eventData) {
    if (Object.keys(eventData)?.includes('xaxis.range')) {
      const startTimestamp = eventData?.['xaxis.range']?.[0];
      const endTimestamp = eventData?.['xaxis.range']?.[1];

      debouncedHandleRangeChange(startTimestamp, endTimestamp);
    } else if (
      Object.keys(eventData)?.includes('xaxis.range[0]') &&
      Object.keys(eventData)?.includes('xaxis.range[1]')
    ) {
      const startTimestamp = eventData?.['xaxis.range[0]'];
      const endTimestamp = eventData?.['xaxis.range[1]'];

      debouncedHandleRangeChange(startTimestamp, endTimestamp);
    };
  };

  function handleClick(e) {
    try {

      const regex = /^(\d{3}-\d{2}-\d{2})/;
      const monitorWellName = e?.points?.[0]?.data?.name?.match(regex)?.[1];
      
      if (monitorWellName) {
        setMonitorUWI(monitorWellName)
      };

      // determine the clicked timestamp and find the corresponding stage
      const clickedTimestamp = new Date(e?.points?.[0]?.x)?.getTime();

      // find the active stage corresponding to the clicked timestamp
      const activeStageName = currentRTFO?.stages?.findLast(s => currentRTFO?.stageData?.[s]?.stageStartTimestamp <= clickedTimestamp);

      if (activeStageName) {
        dispatch(setCurrentStageName(activeStageName));
      };

    } catch(error) {
      console.log(error);
      return;
    };
  };
};

function resampleTimeSeries(data, targetResolution) {
  try {
    if (!data) return []

    // Calculate the number of data points in the target resolution
    const targetDataPoints = Math.floor(data.length / targetResolution)

    // Create a new array to store the resampled data
    const resampledData = []

    for (let i = 0; i < targetDataPoints; i++) {
      const startIndex = i * targetResolution
      const endIndex = startIndex + targetResolution

      // Extract the subset of data for the current target resolution
      const subset = data.slice(startIndex, endIndex)

      // Calculate the average values for the subset
      const averagePressure =
        subset.reduce((sum, element) => sum + element.pressure, 0) /
        targetResolution
      const averageRate =
        subset.reduce((sum, element) => sum + element.rate, 0) /
        targetResolution

      // Calculate the timestamp for the resampled data
      const timestamp = subset[Math.floor(targetResolution / 2)].timestamp

      // Create a new resampled data point
      const resampledPoint = {
        timestamp,
        pressure: averagePressure,
        rate: averageRate,
      }

      // Push the resampled data point to the resampledData array
      resampledData.push(resampledPoint)
    }

    return resampledData
  } catch (error) {
    console.log(error)
    return []
  }
};
